import { useTranslation } from 'react-i18next';
import { telegram } from '../../../utils/telegram';
import { STATUS_FILTER_VALUES } from './constants';
import type { StatusFilterKey, BookingsSummary } from '../../../types/provider';

interface Props {
  value: StatusFilterKey;
  onChange: (value: StatusFilterKey) => void;
  summary: BookingsSummary | null;
}

const CHIP_COLORS: Record<StatusFilterKey, string> = {
  ALL: '#3390ec',
  PENDING: '#faad14',
  CONFIRMED: '#1677ff',
  COMPLETED: '#1f8b4c',
  CANCELED_BY_USER: '#ff4d4f',
  CANCELED_BY_SERVICE_PROVIDER: '#ff4d4f',
};

function getCount(key: StatusFilterKey, summary: BookingsSummary | null): number | null {
  if (!summary) return null;
  switch (key) {
    case 'ALL':
      return summary.total;
    case 'PENDING':
      return summary.pending;
    case 'CONFIRMED':
      return summary.confirmed;
    case 'COMPLETED':
      return summary.completed;
    case 'CANCELED_BY_USER':
    case 'CANCELED_BY_SERVICE_PROVIDER':
      return summary.canceled;
    default:
      return null;
  }
}

export default function StatusFilterChips({ value, onChange, summary }: Props) {
  const { t } = useTranslation('provider');

  const handleChange = (newValue: StatusFilterKey) => {
    if (newValue === value) return;
    telegram.hapticFeedback.selection();
    onChange(newValue);
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: 6,
        overflowX: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        paddingBottom: 2,
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {STATUS_FILTER_VALUES.map((key) => {
        const isActive = value === key;
        const count = getCount(key, summary);
        const color = CHIP_COLORS[key];
        return (
          <button
            key={key}
            onClick={() => handleChange(key)}
            style={{
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              border: isActive ? `1px solid ${color}` : '1px solid var(--bl-divider, #e5e7eb)',
              cursor: 'pointer',
              userSelect: 'none',
              borderRadius: 18,
              padding: '6px 12px',
              fontSize: 12,
              fontWeight: isActive ? 700 : 500,
              background: isActive ? color : 'var(--bl-surface, #fff)',
              color: isActive ? '#fff' : 'var(--bl-text-muted, #6b7280)',
              boxShadow: isActive ? `0 2px 8px ${color}30` : 'none',
              transition: 'background 0.18s ease, color 0.18s ease, border 0.18s ease',
              WebkitTapHighlightColor: 'transparent',
              lineHeight: 1.3,
              letterSpacing: isActive ? 0.05 : 0,
            }}
          >
            {/* Dot indicator (non-ALL) */}
            {key !== 'ALL' && (
              <span
                aria-hidden
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: isActive ? '#fff' : color,
                  flexShrink: 0,
                }}
              />
            )}
            <span>{t(`bookings.statusFilters.${key}`)}</span>
            {count !== null && count > 0 && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  minWidth: 18,
                  padding: '0 5px',
                  height: 16,
                  borderRadius: 8,
                  background: isActive ? 'rgba(255,255,255,0.25)' : color + '14',
                  color: isActive ? '#fff' : color,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
